import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { CalendarSession, DaySessionSummary } from './instructor/calendar/types'

export type InstructorUser = {
  id: string
  instructorID: string
  instructorName: string
  userID: string
  userName: string
  userEmail: string
  createdTime?: string
  updatedTime?: string
}

export type FOAMfratInstructor = {
  id: string
  name: string
  linkedServiceID?: string
  credentials?: string
  createdTime?: string
  updatedTime?: string
}

export type InstructorLinkedUser = {
  instructorUserID: string
  userID: string
  name: string
  email: string
}

export type InstructorLinkReportRow = {
  instructorID: string
  name: string
  credentials?: string
  isLinked: boolean
  linkedUsers: InstructorLinkedUser[]
  eventCount: number
  sessionCount: number
  upcomingSessionCount: number
  nextSessionTime?: string
}

export type InstructorLinkReport = {
  totalInstructors: number
  linkedInstructors: number
  unlinkedInstructors: number
  unlinkedInstructorsWithUpcomingSessions: number
  instructors: InstructorLinkReportRow[]
}

type InstructorState = {
  instructorUsers: InstructorUser[]
  instructorUsersStatus: Status
  foamfratInstructors: FOAMfratInstructor[]
  foamfratInstructorsStatus: Status
  instructorLinkStatus: Status
  instructorLinkError: string
  instructorUnlinkStatus: Status
  instructorUnlinkError: string
  instructorSessions: CalendarSession[]
  instructorSessionsStatus: Status
  calendarSessionsByMonth: Record<string, Record<string, DaySessionSummary>>
  instructorLinkReport: InstructorLinkReport | null
  instructorLinkReportStatus: Status
}

// Selector root type — once this slice is registered in root-state.ts at key
// "instructor", replace this with AppRootState from 'src/store/store'
type InstructorRootState = { instructor: InstructorState }

export let fetchInstructorUsers = createAsyncThunk(
  'instructor/fetchInstructorUsers',
  () =>
    axios
      .get<InstructorUser[]>('/api/pipeline/instructor/instructor-users')
      .then(pluckData),
)

export let fetchFOAMfratInstructors = createAsyncThunk(
  'instructor/fetchFOAMfratInstructors',
  () =>
    axios
      .get<FOAMfratInstructor[]>(
        '/api/pipeline/instructor/foamfrat-instructors',
      )
      .then(pluckData),
)

export let createInstructor = createAsyncThunk(
  'instructor/createInstructor',
  (data: { name: string; credentials?: string }) =>
    axios
      .post<{
        id: string
        status: string
        name: string
      }>('/api/pipeline/instructor', data)
      .then(pluckData),
)

export let linkInstructorToUser = createAsyncThunk(
  'instructor/linkInstructorToUser',
  (data: { instructorID: string; userID: string }) =>
    axios
      .post<{
        id: string
        status: string
        message: string
      }>('/api/pipeline/instructor/link-user', data)
      .then(pluckData),
)

export let unlinkInstructorUser = createAsyncThunk(
  'instructor/unlinkInstructorUser',
  (instructorUserID: string) =>
    axios
      .delete<{
        status: string
        message: string
      }>(`/api/pipeline/instructor/unlink-user/${instructorUserID}`)
      .then(pluckData),
)

export let fetchInstructorLinkReport = createAsyncThunk(
  'instructor/fetchInstructorLinkReport',
  () =>
    axios
      .get<InstructorLinkReport>('/api/pipeline/instructor/link-report')
      .then(pluckData),
)

export let fetchInstructorSessions = createAsyncThunk(
  'instructor/fetchInstructorSessions',
  () =>
    axios
      .get<
        {
          id: string
          name: string | null
          eventID: string
          eventName: string
          sessionTime: string
          endTime: string | null
          timeZone: string
          instructorIDs: string[]
          invites: any[]
          createdTime: string | null
          updatedTime: string | null
        }[]
      >('/api/pipeline/instructor/sessions')
      .then(pluckData)
      .then((sessions) =>
        sessions.map((s): CalendarSession => ({
          id: s.id,
          ...(s.name && { name: s.name }),
          eventID: s.eventID,
          eventName: s.eventName,
          sessionTime: s.sessionTime,
          ...(s.endTime && { endTime: s.endTime }),
          timeZone: s.timeZone,
          instructorIDs: s.instructorIDs,
          instructorNames: [],
          isUserSession: true,
        })),
      ),
)

export let fetchAllInstructorSessions = createAsyncThunk(
  'instructor/fetchAllInstructorSessions',
  () =>
    axios
      .get<
        {
          id: string
          name: string | null
          eventID: string
          eventName: string
          sessionTime: string
          endTime: string | null
          timeZone: string
          instructorIDs: string[]
          invites: any[]
          createdTime: string | null
          updatedTime: string | null
        }[]
      >('/api/pipeline/instructor/all-sessions')
      .then(pluckData)
      .then((sessions) =>
        sessions.map((s): CalendarSession => ({
          id: s.id,
          ...(s.name && { name: s.name }),
          eventID: s.eventID,
          eventName: s.eventName,
          sessionTime: s.sessionTime,
          ...(s.endTime && { endTime: s.endTime }),
          timeZone: s.timeZone,
          instructorIDs: s.instructorIDs,
          instructorNames: [],
          isUserSession: false,
        })),
      ),
)

export let fetchSpreadsheetData = createAsyncThunk(
  'instructor/fetchSpreadsheetDataStatus',
  async (args: { startDate?: string; endDate?: string }) => {
    let { startDate } = args
    let { endDate } = args
    let finalCSV = await axios
      .get<string>(
        `/api/pipeline/ancc-spreadsheet-data?startDate=${startDate}&endDate=${endDate}`,
      )
      .then(pluckData)

    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'course-data.csv'
    a.click()
    URL.revokeObjectURL(url)

    return finalCSV
  },
)

export let initialState: InstructorState = {
  instructorUsers: [],
  instructorUsersStatus: 'uninitialized',
  foamfratInstructors: [],
  foamfratInstructorsStatus: 'uninitialized',
  instructorLinkStatus: 'uninitialized',
  instructorLinkError: '',
  instructorUnlinkStatus: 'uninitialized',
  instructorUnlinkError: '',
  instructorSessions: [],
  instructorSessionsStatus: 'uninitialized',
  calendarSessionsByMonth: {},
  instructorLinkReport: null,
  instructorLinkReportStatus: 'uninitialized',
}

export let instructorSlice = createSlice({
  name: 'instructor',
  initialState,
  reducers: {
    clearInstructorLinkStatus: (state) => {
      state.instructorLinkStatus = 'uninitialized'
      state.instructorLinkError = ''
    },
    clearInstructorUnlinkStatus: (state) => {
      state.instructorUnlinkStatus = 'uninitialized'
      state.instructorUnlinkError = ''
    },
  },
  extraReducers: (builder) => {
    builder
      // Instructor User Mapping Reducers
      .addCase(fetchInstructorUsers.pending, (state) => {
        state.instructorUsersStatus = 'pending'
      })
      .addCase(fetchInstructorUsers.rejected, (state) => {
        state.instructorUsersStatus = 'rejected'
      })
      .addCase(fetchInstructorUsers.fulfilled, (state, action) => {
        state.instructorUsersStatus = 'fulfilled'
        state.instructorUsers = action.payload
      })
      .addCase(fetchFOAMfratInstructors.pending, (state) => {
        state.foamfratInstructorsStatus = 'pending'
      })
      .addCase(fetchFOAMfratInstructors.rejected, (state) => {
        state.foamfratInstructorsStatus = 'rejected'
      })
      .addCase(fetchFOAMfratInstructors.fulfilled, (state, action) => {
        state.foamfratInstructorsStatus = 'fulfilled'
        state.foamfratInstructors = action.payload
      })
      .addCase(createInstructor.pending, (state) => {
        state.foamfratInstructorsStatus = 'pending'
      })
      .addCase(createInstructor.rejected, (state) => {
        state.foamfratInstructorsStatus = 'rejected'
      })
      .addCase(createInstructor.fulfilled, (state, action) => {
        state.foamfratInstructorsStatus = 'fulfilled'
        state.foamfratInstructors = [
          ...state.foamfratInstructors,
          {
            id: action.payload.id,
            name: action.payload.name,
          },
        ]
      })
      .addCase(linkInstructorToUser.pending, (state) => {
        state.instructorLinkStatus = 'pending'
        state.instructorLinkError = ''
      })
      .addCase(linkInstructorToUser.rejected, (state, action) => {
        state.instructorLinkStatus = 'rejected'
        state.instructorLinkError =
          action.error.message || 'Failed to link instructor to user'
      })
      .addCase(linkInstructorToUser.fulfilled, (state) => {
        state.instructorLinkStatus = 'fulfilled'
        state.instructorLinkError = ''
      })
      .addCase(unlinkInstructorUser.pending, (state) => {
        state.instructorUnlinkStatus = 'pending'
        state.instructorUnlinkError = ''
      })
      .addCase(unlinkInstructorUser.rejected, (state, action) => {
        state.instructorUnlinkStatus = 'rejected'
        state.instructorUnlinkError =
          action.error.message || 'Failed to unlink instructor from user'
      })
      .addCase(unlinkInstructorUser.fulfilled, (state) => {
        state.instructorUnlinkStatus = 'fulfilled'
        state.instructorUnlinkError = ''
      })
      .addCase(fetchInstructorLinkReport.pending, (state) => {
        state.instructorLinkReportStatus = 'pending'
      })
      .addCase(fetchInstructorLinkReport.rejected, (state) => {
        state.instructorLinkReportStatus = 'rejected'
      })
      .addCase(fetchInstructorLinkReport.fulfilled, (state, action) => {
        state.instructorLinkReportStatus = 'fulfilled'
        state.instructorLinkReport = action.payload
      })
      // Instructor Sessions reducers
      .addCase(fetchInstructorSessions.pending, (state) => {
        state.instructorSessionsStatus = 'pending'
      })
      .addCase(fetchInstructorSessions.rejected, (state) => {
        state.instructorSessionsStatus = 'rejected'
      })
      .addCase(fetchInstructorSessions.fulfilled, (state, action) => {
        state.instructorSessionsStatus = 'fulfilled'
        state.instructorSessions = action.payload
      })
      .addCase(fetchAllInstructorSessions.pending, (state) => {
        state.instructorSessionsStatus = 'pending'
      })
      .addCase(fetchAllInstructorSessions.rejected, (state) => {
        state.instructorSessionsStatus = 'rejected'
      })
      .addCase(fetchAllInstructorSessions.fulfilled, (state, action) => {
        state.instructorSessionsStatus = 'fulfilled'
        state.instructorSessions = action.payload
      })
  },
})

export let { clearInstructorLinkStatus, clearInstructorUnlinkStatus } =
  instructorSlice.actions

export let selectInstructorUsers = (state: InstructorRootState) =>
  state.instructor.instructorUsers
export let selectInstructorUsersStatus = (state: InstructorRootState) =>
  state.instructor.instructorUsersStatus
export let selectCurrentUserInstructorLink =
  (userID: string) => (state: InstructorRootState) =>
    state.instructor.instructorUsers.find((iu) => iu.userID === userID) || null
export let selectFOAMfratInstructors = (state: InstructorRootState) =>
  state.instructor.foamfratInstructors
export let selectFOAMfratInstructorsStatus = (state: InstructorRootState) =>
  state.instructor.foamfratInstructorsStatus
export let selectInstructorLinkStatus = (state: InstructorRootState) =>
  state.instructor.instructorLinkStatus
export let selectInstructorLinkError = (state: InstructorRootState) =>
  state.instructor.instructorLinkError
export let selectInstructorUnlinkStatus = (state: InstructorRootState) =>
  state.instructor.instructorUnlinkStatus
export let selectInstructorUnlinkError = (state: InstructorRootState) =>
  state.instructor.instructorUnlinkError
export let selectInstructorSessions = (state: InstructorRootState) =>
  state.instructor.instructorSessions
export let selectInstructorSessionsStatus = (state: InstructorRootState) =>
  state.instructor.instructorSessionsStatus
export let selectInstructorLinkReport = (state: InstructorRootState) =>
  state.instructor.instructorLinkReport
export let selectInstructorLinkReportStatus = (state: InstructorRootState) =>
  state.instructor.instructorLinkReportStatus

export default instructorSlice.reducer
